/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useRef } from 'react';
import { Outlet } from 'react-router';
import type { Route } from './+types/_app';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { type ShopperProducts } from '@/scapi';
import { fetchCategory, fetchCategoriesByIds } from '@/lib/api/categories.server';
import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { getLogger } from '@/lib/logger.server';
import Header from '@/components/header';
import Footer from '@/components/footer';
import ResponsiveNavigationMenu from '@/components/navigation-menu-mega';
import { WishlistMergeToast } from '@/components/wishlist/wishlist-merge-toast';

type LoaderData = {
    root: Promise<ShopperProducts.schemas['Category']>;
    subs: Promise<ShopperProducts.schemas['Category'][]>;
    footerPaymentBadges: Promise<PageWithComponentData | null>;
    slotFooterColumn: Promise<PageWithComponentData | null>;
    slotFooterAccount: Promise<PageWithComponentData | null>;
    slotFooterSupport: Promise<PageWithComponentData | null>;
    slotFooterAbout: Promise<PageWithComponentData | null>;
    slotFooterContact: Promise<PageWithComponentData | null>;
    footerSlotsAny: Promise<boolean>;
    slotFooterCopy: Promise<PageWithComponentData | null>;
    // Awaited: the top header banner sits above everything on every route (CLS) and is often the mobile LCP element.
    headerBannerPage: PageWithComponentData | null;
};

/**
 * We're using the `shouldRevalidate` functionality to only load the navigation menu categories from the server on the
 * very first navigation to a page using the (default) `_app` layout. Those categories requests are divided into two
 * discrete request phases; the first to only fetch the root categories, and subsequent multiple calls to fetch the
 * identified sub categories to be displayed in the navigation menu. This behavior ensures that the initial data
 * doesn't get overwritten/removed on subsequent client-side navigations.
 *
 * **Note:** If there is a desire/intent to customize that behavior, the `shouldRevalidate` function can be modified
 * accordingly. For example, a client-side storage for the categories could be introduced to implement any desired
 * caching strategy, including specific reload/refresh intervals/conditions.
 * @see {@link https://reactrouter.com/start/framework/route-module#shouldrevalidate}
 */
export function shouldRevalidate() {
    return false;
}

export async function loader(args: Route.LoaderArgs): Promise<LoaderData> {
    const { context } = args;
    const logger = getLogger(context);
    const config = getConfig(context);
    const { rootCategoryId, maxDepth } = config.pages.navigation;

    logger.debug('AppLayout: loader starting', { rootCategoryId, maxDepth });

    // Load the root category and its sub categories information
    // Depth 1 fetches the root category with its immediate children
    const rootCategoryPromise = fetchCategory(context, rootCategoryId, 1);

    // Load each second-level sub categories tree as well, in case the resolved root-level category has any sub
    // categories and maxDepth allows for it. We then base this composed second-level promise on the initial root
    // category promise to allow for parallel loading and streaming of the two main promises.
    const subCategoriesPromise =
        maxDepth >= 2
            ? rootCategoryPromise.then((rootCategory: ShopperProducts.schemas['Category']) =>
                  // One batched getCategories request for every top-level category that has subcategories,
                  // instead of one getCategory request per category.
                  fetchCategoriesByIds(
                      context,
                      (rootCategory.categories ?? [])
                          .filter(
                              (subCategory: ShopperProducts.schemas['Category']) =>
                                  typeof subCategory.onlineSubCategoriesCount === 'number' &&
                                  subCategory.onlineSubCategoriesCount > 0
                          )
                          .map((subCategory: ShopperProducts.schemas['Category']) => subCategory.id),
                      maxDepth as 0 | 1 | 2
                  )
              )
            : Promise.resolve([]);

    // The mega-menu dropdown's Page Designer content slots (`menu-{categoryId}` / `menu-{categoryId}-ribbon`) are
    // not loaded here: they're fetched on demand when a dropdown opens (resource.nav-content-slot), instead of two
    // SCAPI requests per top-level category on every document request.

    // Business-Manager-editable payment badges for the footer's Contact Us column.
    // Falls back to static badge icons when this content-slot page doesn't exist yet.
    const footerPaymentBadgesPromise = fetchPageWithComponentData(args, { pageId: 'footer-payment-badges' });

    // Business-Manager-editable footer content slots. Each falls back to the existing
    // static footer markup when its content-slot page doesn't exist yet in Business Manager.
    const slotFooterColumn = fetchPageWithComponentData(args, { pageId: 'sfnext-footer-column' }); // Branding & Social Grid
    const slotFooterAccount = fetchPageWithComponentData(args, { pageId: 'sfnext-footer-account' }); // NavColumns "account" column
    const slotFooterSupport = fetchPageWithComponentData(args, { pageId: 'sfnext-footer-support' }); // NavColumns "customerSupport" column
    const slotFooterAbout = fetchPageWithComponentData(args, { pageId: 'sfnext-footer-about' }); // NavColumns "about" column
    const slotFooterContact = fetchPageWithComponentData(args, { pageId: 'sfnext-footer-contact' }); // NavColumns "contactUs" column
    const footerSlotsAny = Promise.all([slotFooterAccount, slotFooterSupport, slotFooterAbout, slotFooterContact]).then(
        (slots) => slots.some(Boolean)
    );
    const slotFooterCopy = fetchPageWithComponentData(args, { pageId: 'sfnext-footer-copy' }); // overrides LegalLinks
    const deferred: Omit<LoaderData, 'headerBannerPage'> = {
        root: rootCategoryPromise,
        subs: subCategoriesPromise,
        footerPaymentBadges: footerPaymentBadgesPromise,
        slotFooterColumn,
        slotFooterAccount,
        slotFooterSupport,
        slotFooterAbout,
        slotFooterContact,
        footerSlotsAny,
        slotFooterCopy,
    };

    // Mark every deferred promise as handled — before awaiting anything below. If the response stream ends first
    // (streamTimeout in entry.server.tsx), React Router stops observing pending promises, and a late rejection (e.g.
    // a SCAPI 429 on a nav category) would otherwise be an unhandled rejection that crashes the server process.
    // Consumers still receive the original promise and observe the rejection as before.
    for (const [key, promise] of Object.entries(deferred)) {
        promise.catch((error: unknown) => {
            logger.warn('AppLayout: deferred loader data failed', {
                key,
                error: error instanceof Error ? error.message : String(error),
            });
        });
    }

    // The top header banner is awaited so it is part of the initial HTML: streaming it in above the header shifted
    // every route's layout (CLS). Only its `topheaderbanner` region's component loaders run — the homepage's other
    // regions are never rendered here. A failed fetch renders no banner rather than failing the layout (SCAPI errors
    // already resolve to null; this covers network errors/timeouts).
    const headerBannerPage = await fetchPageWithComponentData(
        args,
        { pageId: 'sfnext-homepage' },
        { regionIds: ['topheaderbanner'] }
    ).catch((error: unknown) => {
        logger.error('AppLayout: header banner page fetch failed', {
            error: error instanceof Error ? error.message : String(error),
        });
        return null;
    });

    return { ...deferred, headerBannerPage };
}

/**
 * Default Layout Route
 *
 * This pathless layout route provides the standard storefront UI structure:
 * - Header with navigation
 * - Main content area (via `<Outlet/>`)
 * - Footer
 *
 * Routes that need this layout should be prefixed with `_app.` in their filename.
 * For routes without default header/footer (e.g., login), use the `_empty.` prefix instead.
 */
export default function DefaultLayout({
    loaderData: {
        root,
        subs,
        footerPaymentBadges,
        slotFooterColumn,
        slotFooterAccount,
        slotFooterSupport,
        slotFooterAbout,
        slotFooterContact,
        footerSlotsAny,
        slotFooterCopy,
        headerBannerPage,
    },
}: {
    loaderData: LoaderData;
}) {
    const refRoot = useRef<Promise<ShopperProducts.schemas['Category']> | undefined>(undefined);
    const refSubs = useRef<Promise<ShopperProducts.schemas['Category'][]> | undefined>(undefined);
    if (!refRoot.current && !refSubs.current) {
        refRoot.current = root;
        refSubs.current = subs;
    }

    // <WishlistMergeToast> stays at the app shell — it reads URL params and a one-time
    // cookie set by the post-login redirect target, not wishlist state. Routes that need
    // wishlist hooks mount their own <DeferredWishlistProvider> so the SCAPI call only
    // fires on pages that actually render wishlist UI.
    return (
        <>
            <WishlistMergeToast />
            <Header headerBannerPage={headerBannerPage}>
                <ResponsiveNavigationMenu resolve={refRoot.current} defer={refSubs.current} />
            </Header>
            <main className="grow pt-8">
                <Outlet />
            </main>
            <Footer
                paymentBadgesPage={footerPaymentBadges}
                slotFooterColumn={slotFooterColumn}
                slotFooterAccount={slotFooterAccount}
                slotFooterSupport={slotFooterSupport}
                slotFooterAbout={slotFooterAbout}
                slotFooterContact={slotFooterContact}
                footerSlotsAny={footerSlotsAny}
                slotFooterCopy={slotFooterCopy}
            />
        </>
    );
}
